import {Alert, Badge, Button, Group, Modal, NumberInput, Paper, Select, Stack, Table, Text, TextInput, Title} from "@mantine/core";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useContext, useMemo, useState} from "react";
import Page from "../../components/Page/Page.tsx";
import MessageContext from "../../context/MessageContext.tsx";
import api from "../../utils/api.ts";
import {numFormat} from "../../utils/formatters.ts";

interface UserOption {
    uid: string;
    handle: string;
}

interface FactionOption {
    id: string;
    name: string;
}

interface CreateModulePayload {
    ownerType: 'user' | 'faction';
    owner: string;
    type: 'production' | 'mining';
    name: string;
    annualPrice: number;
    billingExpire: string | null;
}

interface UpdateModulePayload {
    id: string;
    name: string;
    annualPrice: number;
    billingExpire: string | null;
}

interface AdminModuleSummary {
    id: string;
    type: string;
    label: string;
    owner: string;
    ownerType: 'user' | 'faction';
    active: boolean;
    accessCount: number;
    trackedEntitiesCount: number;
    annualPrice: number;
    billingExpire: string | null;
    expired: boolean;
    paymentsCount: number;
    paymentLink: string | null;
}

interface AdminModuleListResponse {
    modules: AdminModuleSummary[];
}

// Returns a YYYY-MM-DD string for a native date input, or '' when there is no date.
function toDateInput(iso?: string | null): string {
    if (!iso) {
        return '';
    }
    return new Date(iso).toLocaleDateString('en-CA');
}

function defaultTrialDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toLocaleDateString('en-CA');
}

export default function ModulesAdminPage() {
    const {success, error} = useContext(MessageContext);
    const queryClient = useQueryClient();
    const [ownerType, setOwnerType] = useState<'user' | 'faction'>('user');
    const [owner, setOwner] = useState<string | null>(null);
    const [type, setType] = useState<'production' | 'mining'>('production');
    const [name, setName] = useState('');
    const [annualPrice, setAnnualPrice] = useState<number | string>(0);
    const [billingExpire, setBillingExpire] = useState<string>(defaultTrialDate());

    const [editingModule, setEditingModule] = useState<AdminModuleSummary | null>(null);
    const [editName, setEditName] = useState('');
    const [editPrice, setEditPrice] = useState<number | string>(0);
    const [editBilling, setEditBilling] = useState<string>('');

    const usersQuery = useQuery<UserOption[]>({
        queryKey: ['users', 'public'],
        queryFn: async () => (await api.get('/users')).data as UserOption[],
        staleTime: 1000 * 60 * 5,
    });

    const factionsQuery = useQuery<FactionOption[]>({
        queryKey: ['admin-factions-list'],
        queryFn: async () => (await api.get('/admin/factions')).data as FactionOption[],
    });

    const modulesQuery = useQuery<AdminModuleSummary[]>({
        queryKey: ['admin-modules'],
        queryFn: async () => {
            const response = await api.get('/admin/modules');
            return (response.data as AdminModuleListResponse).modules ?? [];
        },
    });

    const ownerOptions = useMemo(() => {
        if (ownerType === 'user') {
            return (usersQuery.data ?? [])
                .map((item) => ({value: item.uid, label: item.handle}))
                .sort((a, b) => a.label.localeCompare(b.label));
        }

        return (factionsQuery.data ?? [])
            .map((item) => ({value: item.id, label: item.name}))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [ownerType, usersQuery.data, factionsQuery.data]);

    const createMutation = useMutation({
        mutationFn: async (payload: CreateModulePayload) => {
            return (await api.post('/admin/module', payload)).data;
        },
        onSuccess: (response: any) => {
            success('Module created', response?.module?.id ? `ID: ${response.module.id}` : undefined);
            setName('');
            setAnnualPrice(0);
            setBillingExpire(defaultTrialDate());
            queryClient.invalidateQueries({queryKey: ['admin-modules']});
        },
        onError: (e: any) => {
            error('Could not create module', e?.response?.data?.error ?? e.message);
        },
    });

    const updateMutation = useMutation({
        mutationFn: async (payload: UpdateModulePayload) => {
            const {id, ...body} = payload;
            return (await api.post(`/admin/module/${id}`, body)).data;
        },
        onSuccess: () => {
            success('Module updated');
            setEditingModule(null);
            queryClient.invalidateQueries({queryKey: ['admin-modules']});
        },
        onError: (e: any) => {
            error('Could not update module', e?.response?.data?.error ?? e.message);
        },
    });

    const setActiveMutation = useMutation({
        mutationFn: async (payload: {id: string; active: boolean}) => {
            return (await api.post(`/admin/module/${payload.id}/active`, {active: payload.active})).data;
        },
        onSuccess: (_response: any, payload) => {
            success(payload.active ? 'Module enabled' : 'Module disabled');
            queryClient.invalidateQueries({queryKey: ['admin-modules']});
        },
        onError: (e: any) => {
            error('Could not update module', e?.response?.data?.error ?? e.message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (moduleId: string) => {
            await api.delete(`/admin/module/${moduleId}`);
            return moduleId;
        },
        onSuccess: (moduleId: string) => {
            success('Module deleted', moduleId ? `ID: ${moduleId}` : undefined);
            queryClient.invalidateQueries({queryKey: ['admin-modules']});
        },
        onError: (e: any) => {
            error('Could not delete module', e?.response?.data?.error ?? e.message);
        },
    });

    const userLabelMap = useMemo(() => {
        const map: Record<string, string> = {};
        (usersQuery.data ?? []).forEach((user) => {
            map[user.uid] = user.handle;
        });
        return map;
    }, [usersQuery.data]);

    const factionLabelMap = useMemo(() => {
        const map: Record<string, string> = {};
        (factionsQuery.data ?? []).forEach((faction) => {
            map[faction.id] = faction.name;
        });
        return map;
    }, [factionsQuery.data]);

    const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data]);

    const ownerTypeOptions = [
        {value: 'user', label: 'User'},
        {value: 'faction', label: 'Faction'},
    ];

    const moduleTypeOptions = [
        {value: 'production', label: 'Production'},
        {value: 'mining', label: 'Mining'},
    ];

    const isLoadingOwners = usersQuery.isPending || factionsQuery.isPending;
    const canSubmit = !isLoadingOwners && !createMutation.isPending && !!owner && name.trim() !== '' && !!type;

    const resolveOwnerLabel = (module: AdminModuleSummary): string => {
        if (module.ownerType === 'user') {
            return userLabelMap[module.owner] ?? module.owner;
        }
        return factionLabelMap[module.owner] ?? module.owner;
    };

    const handleDelete = (module: AdminModuleSummary) => {
        if (module.active) {
            error('Disable the module first', 'Modules must be disabled before deletion.');
            return;
        }

        const confirmation = window.prompt(`Type "${module.id}" to confirm deletion of ${module.label || module.id}.`);
        if (confirmation === null) {
            return;
        }
        if (confirmation.trim() !== module.id) {
            error('Deletion canceled', 'Confirmation did not match.');
            return;
        }

        deleteMutation.mutate(module.id);
    };

    const openEdit = (module: AdminModuleSummary) => {
        setEditingModule(module);
        setEditName(module.label);
        setEditPrice(module.annualPrice ?? 0);
        setEditBilling(toDateInput(module.billingExpire));
    };

    const renderBilling = (module: AdminModuleSummary) => {
        if (!module.billingExpire) {
            return <Badge color="blue" variant="light">Free</Badge>;
        }
        const date = new Date(module.billingExpire).toLocaleDateString();
        return (
            <Text size="sm" c={module.expired ? 'red' : undefined}>
                {module.expired ? `Expired ${date}` : date}
            </Text>
        );
    };

    return (
        <Page>
            <title>Admin - Modules</title>
            <Stack gap="md">
                <Title order={1}>Create Module</Title>

                <Paper withBorder p="md">
                    <Stack gap="sm">
                        <Select
                            label="Owner Type"
                            value={ownerType}
                            data={ownerTypeOptions}
                            onChange={(value) => {
                                const nextOwnerType = (value as 'user' | 'faction') || 'user';
                                setOwnerType(nextOwnerType);
                                setOwner(null);
                            }}
                            allowDeselect={false}
                        />

                        <Select
                            label="Owner"
                            value={owner}
                            data={ownerOptions}
                            onChange={setOwner}
                            placeholder={isLoadingOwners ? 'Loading owners...' : 'Select owner'}
                            searchable
                            disabled={isLoadingOwners}
                        />

                        <Select
                            label="Module Type"
                            value={type}
                            data={moduleTypeOptions}
                            onChange={(value) => setType((value as 'production' | 'mining') || 'production')}
                            allowDeselect={false}
                        />

                        <TextInput
                            label="Module Name"
                            value={name}
                            onChange={(event) => setName(event.currentTarget.value)}
                            placeholder="Example: Core Foundry"
                        />

                        <NumberInput
                            label="Annual Price"
                            description="In-game credits charged per year. 0 for a free module."
                            value={annualPrice}
                            onChange={setAnnualPrice}
                            min={0}
                            step={1000000}
                            thousandSeparator=","
                        />

                        <TextInput
                            label="Billing Expires"
                            description="Leave empty for a free module with no expiry. Defaults to a 30-day trial."
                            type="date"
                            value={billingExpire}
                            onChange={(event) => setBillingExpire(event.currentTarget.value)}
                        />

                        <Button
                            onClick={() => {
                                if (!owner || name.trim() === '') {
                                    return;
                                }

                                createMutation.mutate({
                                    ownerType,
                                    owner,
                                    type,
                                    name: name.trim(),
                                    annualPrice: Number(annualPrice) || 0,
                                    billingExpire: billingExpire || null,
                                });
                            }}
                            loading={createMutation.isPending}
                            disabled={!canSubmit}
                        >
                            Create Module
                        </Button>
                    </Stack>
                </Paper>

                <Title order={2}>Modules</Title>
                <Paper withBorder p="md">
                    {modulesQuery.isPending ? (
                        <Text>Loading modules...</Text>
                    ) : modulesQuery.isError ? (
                        <Alert color="red" title="Could not load modules">
                            {(modulesQuery.error as any)?.message ?? 'The modules list is currently unavailable.'}
                        </Alert>
                    ) : (
                        <Table withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Module</Table.Th>
                                    <Table.Th>Type</Table.Th>
                                    <Table.Th>Owner</Table.Th>
                                    <Table.Th>Access Users</Table.Th>
                                    <Table.Th>Tracked Entities</Table.Th>
                                    <Table.Th>Price</Table.Th>
                                    <Table.Th>Billing</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {modules.map((module) => {
                                    const ownerLabel = resolveOwnerLabel(module);
                                    const label = module.label || `Module ${module.id}`;
                                    const isUpdatingActive = setActiveMutation.isPending && setActiveMutation.variables?.id === module.id;
                                    const isDeleting = deleteMutation.isPending && deleteMutation.variables === module.id;

                                    return (
                                        <Table.Tr key={module.id}>
                                            <Table.Td>
                                                <Text fw={600}>{label}</Text>
                                                <Text size="xs" c="dimmed">{module.id}</Text>
                                            </Table.Td>
                                            <Table.Td>{module.type}</Table.Td>
                                            <Table.Td>
                                                <Text>{ownerLabel}</Text>
                                                <Text size="xs" c="dimmed">{module.ownerType}</Text>
                                            </Table.Td>
                                            <Table.Td>{module.accessCount}</Table.Td>
                                            <Table.Td>{module.trackedEntitiesCount}</Table.Td>
                                            <Table.Td>{module.annualPrice > 0 ? numFormat.format(module.annualPrice) : '—'}</Table.Td>
                                            <Table.Td>{renderBilling(module)}</Table.Td>
                                            <Table.Td>
                                                <Badge color={module.active ? 'green' : 'gray'} variant="light">
                                                    {module.active ? 'Active' : 'Disabled'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <Button
                                                        size="compact-xs"
                                                        variant="light"
                                                        onClick={() => openEdit(module)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="compact-xs"
                                                        variant="light"
                                                        color={module.active ? 'orange' : 'green'}
                                                        loading={isUpdatingActive}
                                                        disabled={isDeleting}
                                                        onClick={() => setActiveMutation.mutate({id: module.id, active: !module.active})}
                                                    >
                                                        {module.active ? 'Disable' : 'Enable'}
                                                    </Button>
                                                    <Button
                                                        size="compact-xs"
                                                        variant="light"
                                                        color="red"
                                                        disabled={module.active || isDeleting || isUpdatingActive}
                                                        loading={isDeleting}
                                                        onClick={() => handleDelete(module)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {modules.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={9}>
                                            <Text c="dimmed">No modules found.</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : null}
                            </Table.Tbody>
                        </Table>
                    )}
                </Paper>
            </Stack>

            <Modal
                opened={editingModule !== null}
                onClose={() => setEditingModule(null)}
                title={editingModule ? `Edit ${editingModule.label || editingModule.id}` : 'Edit Module'}
            >
                <Stack gap="sm">
                    <TextInput
                        label="Module Name"
                        value={editName}
                        onChange={(event) => setEditName(event.currentTarget.value)}
                    />
                    <NumberInput
                        label="Annual Price"
                        description="In-game credits charged per year. 0 for a free module."
                        value={editPrice}
                        onChange={setEditPrice}
                        min={0}
                        step={1000000}
                        thousandSeparator=","
                    />
                    <TextInput
                        label="Billing Expires"
                        description="Leave empty for a free module with no expiry."
                        type="date"
                        value={editBilling}
                        onChange={(event) => setEditBilling(event.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditingModule(null)}>Cancel</Button>
                        <Button
                            loading={updateMutation.isPending}
                            disabled={editName.trim() === ''}
                            onClick={() => {
                                if (!editingModule || editName.trim() === '') {
                                    return;
                                }
                                updateMutation.mutate({
                                    id: editingModule.id,
                                    name: editName.trim(),
                                    annualPrice: Number(editPrice) || 0,
                                    billingExpire: editBilling || null,
                                });
                            }}
                        >
                            Save
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Page>
    );
}
